import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { createLazyComponent, LazyWrapper } from './components/LazyWrapper';
import ResetPassword from './modules/auth/ResetPassword';
import VerifyAccount from './modules/auth/VerifyAccount';
import { useAppSelector } from './hooks/redux';

// -------- Route constants (single source of truth) --------
const ROUTES = {
	ROOT: '/',
	ESIGNATURES: '/esignatures',
	ESIGNATURE_DETAIL: '/esignatures/:id',
	SIGN: '/sign/:token',
	TASKS: '/tasks',
	TASKS_ADD_TEMPLATE: '/tasks/weekly-task-board',
	TASK_ADD_TEMPLATE: '/task/weekly-task-board',
	LOGIN: '/login',
	RESET_PW: '/forgot-password/:token/:email',
	VERIFY: '/verify/:token',

	DASHBOARD: '/dashboard',
	USERS: '/users',
	USER_DETAIL: '/users/:id',
	TEAM: '/team',

	CLIENTS: '/clients',
	CLIENT_DETAIL: '/clients/:id',

	COS_DASHBOARD: '/cos-dashboard',

	SC_ROOT: '/sc',
	SC_DASHBOARD: '/sc/dashboard',
	// SC_CLIENTS: '/sc/clients',
	SC_COORDINATION: '/sc/coordination',
	SC_CASE_NOTES: '/sc/case-notes',
	SC_CLAIMS: '/sc/claims',
	SC_INVOICES: '/sc/invoices',
	SC_INVOICE_DETAIL: '/sc/invoices/:id',

	REPORTS: '/reports',
	REPORTS_DASHBOARD: '/reports/dashboard',
	REPORTS_CLIENT_FUNDING: '/reports/client-funding',
	REPORTS_CLIENT_PROFILE: '/reports/client-profile',
	REPORTS_CUSTOM: '/reports/custom',
	BILLING: '/billing',
	CONTRACT_DETAIL: '/contracts/:id',
	INTEGRATIONS: '/integrations',
	DOWNLOADS: '/downloads',
	ALERTS: '/alerts',
	SUPPORT: '/support',
	SETTINGS: '/settings',
	TEMPLATES: '/templates',
	PROFILE: '/profile',
	USER_SETTINGS: '/settings/user',
};

// -------- Lazy components --------
const AppLayout = createLazyComponent(() => import('./components/layout/AppLayout'), 'Loading application layout...');
const LoginPage = createLazyComponent(() => import('./modules/auth/LoginPage'), 'Loading login page...');
const UserListPage = createLazyComponent(() => import('./modules/users/UserListPage'), 'Loading users...');
const UserDetailPage = createLazyComponent(() => import('./modules/users/UserDetailPage'), 'Loading users...');
const TeamManagementPage = createLazyComponent(() => import('./modules/users/TeamManagementPage'), 'Loading team...');
const ClientListPage = createLazyComponent(
	() => import('./modules/clients/pages/ClientListPage'),
	'Loading participants...',
);
const TasksPage = createLazyComponent(() => import('./modules/tasks/tasks'), 'Loading Tasks...');
const AddTaskTemplatePage = createLazyComponent(
	() => import('./modules/tasks/AddTaskTemplate'),
	'Loading Add Task UI...',
);
const ClientProfilePage = createLazyComponent(
	() => import('./modules/clients/pages/ClientProfilePage'),
	'Loading participant profile...',
);
const ContractDetailPage = createLazyComponent(
	() => import('./modules/contracts/ContractDetailPage'),
	'Loading contract...',
);
const InvoiceDetailPage = createLazyComponent(
	() => import('./modules/sc/pages/InvoiceDetailPage'),
	'Loading invoice...',
);
const ScLayout = createLazyComponent(() => import('./modules/sc/ScLayout'), 'Loading Support Coordination...');
const CosDashboardPage = createLazyComponent(() => import('./modules/cos-dashboard'), 'Loading CoS Dashboard...');
// const ScClients = createLazyComponent(() => import('./modules/sc/pages/ScClientsPage'), 'Loading SC clients...');

const ScCoordination = createLazyComponent(
	() => import('./modules/sc/pages/CoordinationPage'),
	'Loading Participant Coordination...',
);
const FundingCoordination = createLazyComponent(
	() => import('./modules/sc/pages/FundingCoordinationPage'),
	'Loading Funding Coordination...',
);
const ScCaseNotes = createLazyComponent(() => import('./modules/sc/pages/ScCaseNotesPage'), 'Loading Case Notes...');
const ScClaims = createLazyComponent(() => import('./modules/sc/pages/ScClaimsPage'), 'Loading NDIS Claims...');
const ScInvoices = createLazyComponent(() => import('./modules/sc/pages/ScInvoicesPage'), 'Loading Invoices...');
const ProfilePage = createLazyComponent(() => import('./modules/auth/ProfilePage'), 'Loading profile...');
const DownloadsPage = createLazyComponent(() => import('./modules/downloads/index'), 'Loading downloads...');
const SettingsPage = createLazyComponent(() => import('./modules/settings/SettingsPage'), 'Loading settings...');
const TemplatesPage = createLazyComponent(() => import('./modules/templates/pages/TemplatesPage'), 'Loading templates...');
const UserSettingsPage = createLazyComponent(() => import('./modules/settings/pages/UserSettingsPage'), 'Loading settings...');
const AlertsPage = createLazyComponent(() => import('./modules/alerts/pages/AlertsPage'), 'Loading alerts...');
const SupportAdminPage = createLazyComponent(
	() => import('./modules/support/pages/SupportAdminPage'),
	'Loading support tickets...',
);
const ESignaturesPage = createLazyComponent(
	() => import('./modules/esignatures/pages/ESignaturesPage'),
	'Loading eSignatures...',
);
const SignatureRequestDetailPage = createLazyComponent(
	() => import('./modules/esignatures/pages/SignatureRequestDetailPage'),
	'Loading request details...',
);
const IntakeFormPage = createLazyComponent(
	() => import('./modules/intake/pages/IntakeFormPage'),
	'Loading intake form...',
);
const IntakeCaseDetailPage = createLazyComponent(
	() => import('./modules/intake/pages/IntakeCaseDetailPage'),
	'Loading intake case...',
);
const SignPage = createLazyComponent(
	() => import('./modules/esignatures/pages/SignPage'),
	'Loading signing page...',
);
const AnalyticsDashboard = createLazyComponent(
	() => import('./modules/analytics/pages/AdminDashboardTabs'),
	'Loading analytics dashboard...',
);
const ClientFundingReportPage = createLazyComponent(
	() => import('./modules/reports/pages/ClientFundingReportPage'),
	'Loading participant funding report...',
);
const ReportDashboardPage = createLazyComponent(
	() => import('./modules/reports/pages/ReportDashboardPage'),
	'Loading report dashboard...',
);
const CustomReportPage = createLazyComponent(
	() => import('./modules/reports/pages/CustomReportPage'),
	'Loading custom report...',
);
const ClientProfileReportPage = createLazyComponent(
	() => import('./modules/reports/pages/ClientProfileReportPage'),
	'Loading participant profile report...',
);

const RoleBasedLanding = () => {
	const { user } = useAppSelector((state) => state.auth);
	const userRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
	return <Navigate replace to={userRoles.includes(1) ? ROUTES.DASHBOARD : ROUTES.COS_DASHBOARD} />;
};

const AdminDashboardRoute = () => {
	const { user } = useAppSelector((state) => state.auth);
	const userRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);

	if (userRoles.length > 0 && !userRoles.includes(1)) {
		return <Navigate replace to={ROUTES.COS_DASHBOARD} />;
	}

	return <AnalyticsDashboard />;
};

// All Tasks (the task list view) is Admin-only; everyone else is sent to the Task Board.
// Exception: a direct ?taskId= deep link (e.g. "Edit Task" from a Client's Tasks tab) is
// open to every role today, so it's let through here to avoid breaking that existing
// capability. This route check is only a coarse pre-filter though — TasksPage itself
// (tasks.tsx) refuses to render the list UI or fetch list data for non-admins regardless
// of how it was reached, so a non-admin with an arbitrary ?taskId= still can't see the list.
const AllTasksRoute = ({ taskBoardPath }: { taskBoardPath: string }) => {
	const { user } = useAppSelector((state) => state.auth);
	const [searchParams] = useSearchParams();
	const userRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);

	if (userRoles.length > 0 && !userRoles.some((role) => [1, 3].includes(role)) && !searchParams.has('taskId')) {
		return <Navigate replace to={taskBoardPath} />;
	}

	return <TasksPage />;
};

const AppRoutes = () => {
	return (
		<ErrorBoundary>
			<Routes>
				{/* Public */}
				<Route path={ROUTES.LOGIN} element={<LoginPage />} />
				<Route path={ROUTES.RESET_PW} element={<ResetPassword />} />
				<Route path={ROUTES.VERIFY} element={<VerifyAccount />} />
				<Route path={ROUTES.SIGN} element={<SignPage />} />
				<Route path="/intake" element={<IntakeFormPage />} />

				{/* Protected layout */}
				<Route
					element={
						<ProtectedRoute>
							<AppLayout />
						</ProtectedRoute>
					}
				>
					{/* Root → role-specific landing page */}
					<Route path={ROUTES.ROOT} element={<RoleBasedLanding />} />

					{/* Top-level */}
					<Route
						path={ROUTES.DASHBOARD}
						element={
							<ProtectedRoute requiredMenu="Dashboard">
								<AdminDashboardRoute />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.TASKS}
						element={
							<ProtectedRoute requiredMenu="My Tasks">
								<AllTasksRoute taskBoardPath={ROUTES.TASKS_ADD_TEMPLATE} />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.TASKS_ADD_TEMPLATE}
						element={
							<ProtectedRoute requiredMenu="My Tasks">
								<AddTaskTemplatePage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.TASK_ADD_TEMPLATE}
						element={
							<ProtectedRoute requiredMenu="My Tasks">
								<AddTaskTemplatePage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.USERS}
						element={
							<ProtectedRoute requiredMenu="Users">
								<UserListPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.USER_DETAIL}
						element={
							<ProtectedRoute requiredMenu="Users">
								<UserDetailPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.TEAM}
						element={
							<ProtectedRoute requiredMenu="Teams">
								<TeamManagementPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.TEAM}
						element={
							<ProtectedRoute requiredMenu="Teams">
								<TeamManagementPage />
							</ProtectedRoute>
						}
					/>

					<Route
						path={ROUTES.COS_DASHBOARD}
						element={
							<ProtectedRoute requiredMenu="Support Coordination/Dashboard">
								<CosDashboardPage />
							</ProtectedRoute>
						}
					/>

					{/* Support Coordination (canonical dashboard is /cos-dashboard) */}
					<Route
						path={ROUTES.SC_ROOT}
						element={
							<ProtectedRoute requiredMenu="Support Coordination">
								<ScLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate replace to={ROUTES.COS_DASHBOARD} />} />
						{/* Redirect old SC dashboard path to canonical CoS path */}
						<Route path="dashboard" element={<Navigate to={ROUTES.COS_DASHBOARD} replace />} />

						{/* <Route
							path="clients"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/Clients">
									<ScClients />
								</ProtectedRoute>
							}
						/> */}
						<Route
							path="tasks"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/My Tasks">
									<AllTasksRoute taskBoardPath="/sc/tasks/weekly-task-board" />
								</ProtectedRoute>
							}
						/>
						<Route
							path="tasks/weekly-task-board"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/My Tasks">
									<AddTaskTemplatePage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="coordination"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/Client Coordination">
									<ScCoordination />
								</ProtectedRoute>
							}
						/>
						<Route
							path="funding-coordination"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/Client Coordination">
									<FundingCoordination />
								</ProtectedRoute>
							}
						/>
						<Route
							path="case-notes"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/Case Note History">
									<ScCaseNotes />
								</ProtectedRoute>
							}
						/>
						<Route
							path="claims"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/CoS NDIS Claims">
									<ScClaims />
								</ProtectedRoute>
							}
						/>
						<Route
							path="invoices"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/CoS Invoices">
									<ScInvoices />
								</ProtectedRoute>
							}
						/>
						<Route
							path="invoices/:id"
							element={
								<ProtectedRoute requiredMenu="Support Coordination/CoS Invoices">
									<InvoiceDetailPage />
								</ProtectedRoute>
							}
						/>
					</Route>

					{/* Clients */}
					<Route
						path={ROUTES.CLIENTS}
						element={
							<ProtectedRoute requiredMenu="Clients">
								<ClientListPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.CLIENT_DETAIL}
						element={
							<ProtectedRoute requiredMenu="Clients">
								<ClientProfilePage />
							</ProtectedRoute>
						}
					/>

					{/* Intake (queue lives as a tab on the Participants page, like ndis) */}
					<Route path="/referrals/intake" element={<Navigate replace to="/clients?tab=intake" />} />
					<Route
						path="/referrals/intake/:id"
						element={
							<ProtectedRoute requiredMenu="Clients">
								<IntakeCaseDetailPage />
							</ProtectedRoute>
						}
					/>

					{/* Reports */}
					<Route path={ROUTES.REPORTS} element={<Navigate replace to={ROUTES.REPORTS_DASHBOARD} />} />
					<Route path={ROUTES.REPORTS_DASHBOARD} element={<ProtectedRoute requiredMenu="Reports"><ReportDashboardPage /></ProtectedRoute>} />
					<Route path={ROUTES.REPORTS_CLIENT_FUNDING} element={<ProtectedRoute requiredMenu="Reports"><ClientFundingReportPage /></ProtectedRoute>} />
					<Route path={ROUTES.REPORTS_CLIENT_PROFILE} element={<ProtectedRoute requiredMenu="Reports"><ClientProfileReportPage /></ProtectedRoute>} />
					<Route path={ROUTES.REPORTS_CUSTOM} element={<ProtectedRoute requiredMenu="Reports"><CustomReportPage /></ProtectedRoute>} />

					{/* Billing (stub) */}
					<Route
						path={ROUTES.BILLING}
						element={
							<LazyWrapper fallback={<div style={{ padding: 20 }}>Loading billing...</div>}>
								<div style={{ padding: 20 }}>
									<h2>Billing Page</h2>
									<p>Coming soon...</p>
								</div>
							</LazyWrapper>
						}
					/>

					{/* Contracts */}
					<Route path={ROUTES.CONTRACT_DETAIL} element={<ContractDetailPage />} />

					{/* Integrations (stub) */}
					<Route
						path={ROUTES.INTEGRATIONS}
						element={
							<LazyWrapper fallback={<div style={{ padding: 20 }}>Loading integrations...</div>}>
								<div style={{ padding: 20 }}>
									<h2>Integrations Page</h2>
									<p>Coming soon...</p>
								</div>
							</LazyWrapper>
						}
					/>

					{/* eSignatures */}
					<Route
						path={ROUTES.ESIGNATURE_DETAIL}
						element={
							<ProtectedRoute requiredMenu="eSignatures">
								<SignatureRequestDetailPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.ESIGNATURES}
						element={
							<ProtectedRoute requiredMenu="eSignatures">
								<ESignaturesPage />
							</ProtectedRoute>
						}
					/>

					{/* Templates */}
					<Route
						path={ROUTES.TEMPLATES}
						element={
							<ProtectedRoute requiredMenu="Templates">
								<TemplatesPage />
							</ProtectedRoute>
						}
					/>
					{/* Legacy redirect: /quote-templates → /templates */}
					<Route path="/quote-templates" element={<Navigate replace to={ROUTES.TEMPLATES} />} />

					{/* Misc */}
					<Route
						path={ROUTES.DOWNLOADS}
						element={
							<ProtectedRoute requiredMenu="Downloads">
								<DownloadsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.ALERTS}
						element={
							<ProtectedRoute requiredMenu="Alerts">
								<AlertsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.SUPPORT}
						element={
							<ProtectedRoute requiredMenu="Support">
								<SupportAdminPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path={ROUTES.SETTINGS}
						element={
							<ProtectedRoute requireSuperuser allowSystemAdministrator>
								<SettingsPage />
							</ProtectedRoute>
						}
					/>
					<Route path={ROUTES.PROFILE} element={<ProfilePage />} />
					<Route path={ROUTES.USER_SETTINGS} element={<UserSettingsPage />} />
				</Route>

				{/* 404 */}
				<Route
					path="*"
					element={
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100vh',
								flexDirection: 'column',
							}}
						>
							<h2>404 - Page Not Found</h2>
							<p>The page you're looking for doesn't exist.</p>
						</div>
					}
				/>
			</Routes>
		</ErrorBoundary>
	);
};

export default AppRoutes;
